import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  onFileSelected: (files: File[]) => void;
  onSkippedFiles?: (count: number) => void;
  initialError?: string;
  onTriggerFileSelect?: (trigger: () => void) => void;
};

export const FileSelectStep: React.FC<Props> = ({ onFileSelected, onSkippedFiles, initialError, onTriggerFileSelect }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(initialError ?? null);
  const [dragging, setDragging] = useState(false);

  React.useEffect(() => {
    if (onTriggerFileSelect) {
      onTriggerFileSelect(() => inputRef.current?.click());
    }
  }, [onTriggerFileSelect]);

  const handleFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    const valid = files.filter((f) => f.name.split('.').pop()?.toLowerCase() === 'xlsx');
    const skipped = files.length - valid.length;
    if (valid.length === 0) {
      setError(t('management.import.fileSelect.error'));
      return;
    }
    setError(null);
    // Always report the current run's skipped count — including 0 — so a run
    // with no skipped files clears a stale count left over from a previous
    // run instead of silently keeping it displayed in the next summary.
    onSkippedFiles?.(skipped);
    onFileSelected(valid);
  };

  return (
    <div className="flex flex-col gap-4">
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${dragging ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-300 hover:border-gray-400'}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <p className="text-sm text-gray-500">{t('management.import.fileSelect.dropzone')}</p>
        <p className="text-xs text-gray-400 mt-1">{t('management.import.fileSelect.formatHint')}</p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files ?? []);
          // Clear the input value defensively so re-selecting the exact same
          // file for a second run fires onChange again — browsers otherwise
          // suppress the change event when the value would be unchanged.
          e.target.value = '';
        }}
      />
    </div>
  );
};
